import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { MapContainer, TileLayer, Popup, Marker } from 'react-leaflet';
import Modal from 'reactjs-popup';
import markerIconPng from 'leaflet/dist/images/marker-icon.png';
import { Icon, LatLngExpression } from 'leaflet';

import { icons } from '~/assets';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Dialog from '~/components/dialog';
import Input, { MaskTypes } from '~/components/newInput';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import TableList from '~/components/tableList';
import useApi, { ApiMethod } from '~/hooks/useApi';
import endpoints from '~/services/endpoints';
import { dateMask } from '~/utils/masks';
import * as St from '../styles';
import 'leaflet/dist/leaflet.css';
import 'reactjs-popup/dist/index.css';

interface IForm {
  de: { masked?: string };
  ate: { masked?: string };
}

interface Geolocation {
  latitude?: number;
  longitude?: number;
}

interface PasswordHistoryRecord {
  dataCriacao?: string;
  dataAlteracao?: string;
  dataHora?: string;
  data?: string;
  hora?: string;
  ip?: string;
  enderecoIp?: string;
  canal?: string;
  origem?: string;
  latitude?: number;
  longitude?: number;
  geolocation?: Geolocation;
  localizacao?: Geolocation;
}

interface PasswordHistoryResponse {
  data?: PasswordHistoryRecord[];
  content?: PasswordHistoryRecord[];
  page?: number;
  totalPages?: number;
}

interface PasswordHistoryItem {
  data: string;
  hora: string;
  ip: string;
  canal: string;
  geolocation?: Geolocation;
}

const customerHeader = [
  { id: 1, value: 'Data', maxChar: 10, textAlign: 'center' },
  { id: 2, value: 'Hora', maxChar: 10, textAlign: 'center' },
  { id: 3, value: 'Endereço/IP', maxChar: 16, textAlign: 'center' },
  { id: 4, value: 'Canal', maxChar: 12, textAlign: 'center' },
  { id: 5, value: 'Localização', maxChar: 12, textAlign: 'center' },
];

const toHistoryItem = (record: PasswordHistoryRecord): PasswordHistoryItem => {
  const dateSource =
    record.dataCriacao || record.dataAlteracao || record.dataHora || record.data;
  const parsedDate = dateSource
    ? moment(dateSource, ['DD/MM/YYYY HH:mm:ss', 'DD/MM/YYYY', moment.ISO_8601], true)
    : null;
  const hasDate = parsedDate?.isValid() === true;
  const geolocation =
    record.geolocation ||
    record.localizacao ||
    (record.latitude != null
      ? { latitude: record.latitude, longitude: record.longitude }
      : undefined);

  return {
    data: hasDate ? parsedDate.format('DD/MM/YYYY') : '-',
    hora: record.hora || (hasDate ? parsedDate.format('HH:mm:ss') : '-'),
    ip: record.ip || record.enderecoIp || '-',
    canal: record.canal || record.origem || '-',
    geolocation,
  };
};

const extractRecords = (
  payload: PasswordHistoryResponse | PasswordHistoryRecord[] | null,
) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.content)) return payload.content;
  return [];
};

const PasswordHistory: React.FC = () => {
  const history = useHistory();
  const { state } = useLocation<{ idUsuario?: string }>();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogError, setOpenDialogError] = useState(false);
  const [latLng, setLatLng] = useState<LatLngExpression>([]);
  const [dialogMessage, setDialogMessage] = useState('');
  const [feedback, setFeedback] = useState('');
  const [list, setList] = useState([]);
  const [form, setForm] = useState<IForm>({
    de: dateMask(moment().format('DD/MM/YYYY')),
    ate: dateMask(moment().format('DD/MM/YYYY')),
  });

  const getPasswordHistory = useApi<PasswordHistoryResponse>(
    endpoints.consult.getPasswordChangeHistory.replace(
      'PARAM_ID_USUARIO',
      state?.idUsuario || '',
    ),
    ApiMethod.GET,
    {},
    true,
  );

  const handleChange = ({
    target: { value, name },
  }: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const datesAreComplete =
    form.de?.masked?.length === 10 && form.ate?.masked?.length === 10;

  const openLocation = (item: PasswordHistoryItem) => {
    if (item?.geolocation?.latitude) {
      setLatLng([
        item.geolocation.latitude ?? 0,
        item.geolocation.longitude ?? 0,
      ]);
      setOpenDialog(true);
      return;
    }

    setDialogMessage('Localização indisponível');
    setOpenDialogError(true);
  };

  const buildRows = (items: PasswordHistoryItem[]) =>
    items.map((item) => [
      {
        id: 1,
        border: true,
        title: 'Data',
        value: item.data,
        maxChar: 20,
        textAlign: 'center',
      },
      {
        id: 2,
        border: true,
        title: 'Hora',
        value: item.hora,
        maxChar: 15,
        textAlign: 'center',
      },
      {
        id: 3,
        border: true,
        title: 'Endereço/IP',
        value: item.ip,
        maxChar: 16,
        textAlign: 'center',
      },
      {
        id: 4,
        border: true,
        title: 'Canal',
        value: item.canal,
        maxChar: 12,
        textAlign: 'center',
      },
      {
        id: 5,
        item,
        searchDisabled: !item.geolocation?.latitude,
        searchAction: () => openLocation(item),
      },
    ]);

  const handleConsult = async (page = 1) => {
    if (!datesAreComplete || !form.de.masked || !form.ate.masked) return;

    if (!state?.idUsuario) {
      setFeedback('Usuário não informado para a consulta.');
      setList([]);
      return;
    }

    setFeedback('');

    try {
      const response = await getPasswordHistory.callApi({
        limit: 10,
        page,
        dataInicial: form.de.masked.split('/').reverse().join('-'),
        dataFinal: form.ate.masked.split('/').reverse().join('-'),
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setList([]);
        setFeedback(
          response.mensagem || 'Não foi possível consultar o histórico.',
        );
        return;
      }

      const records = extractRecords(response.data);
      setList(buildRows(records.map(toHistoryItem)));
    } catch (error) {
      setList([]);
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar o histórico.',
      );
    }
  };

  const showMap = useMemo(
    () => (
      <MapContainer
        id="map"
        style={{ height: '400px' }}
        center={latLng}
        zoom={20}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker
          icon={
            new Icon({
              iconUrl: markerIconPng,
              iconSize: [25, 41],
              iconAnchor: [12, 41],
            })
          }
          alt="marcador"
          draggable={false}
          position={latLng}
        >
          <Popup>Localização</Popup>
        </Marker>
      </MapContainer>
    ),
    [latLng],
  );

  return (
    <>
      <Loading status={getPasswordHistory.loading} />
      <Modal
        position="center center"
        closeOnDocumentClick
        onClose={() => setOpenDialog(false)}
        open={openDialog}
      >
        <ContainerFlex row center horizontal>
          <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 8 }}>
            Localização
          </p>
        </ContainerFlex>
        {latLng.length > 0 && showMap}
        <ContainerFlex row end marginBottom={8} marginTop={16}>
          <Button onClick={() => setOpenDialog(false)}>voltar</Button>
        </ContainerFlex>
      </Modal>
      <Dialog
        title="Localização"
        open={openDialogError}
        icon={icons.warning}
        positiveLabel="Entendi"
        positiveAction={() => setOpenDialogError(false)}
        message={dialogMessage}
      />

      <Container>
        <St.BoxCard>
          <ContainerFlex row space>
            <ContainerFlex padding={16}>
              <Input
                label="De"
                name="de"
                value={form.de?.masked}
                mask={MaskTypes.DATE}
                onChange={handleChange}
                maxLength={10}
              />
            </ContainerFlex>
            <ContainerFlex padding={16}>
              <Input
                label="Até"
                name="ate"
                mask={MaskTypes.DATE}
                value={form.ate?.masked}
                onChange={handleChange}
                maxLength={10}
              />
            </ContainerFlex>
            <ContainerFlex padding={16}>
              <ContainerFlex row space gap="8">
                <ContainerFlex>
                  <Button
                    fitContent
                    onClick={() => handleConsult()}
                    disabled={!datesAreComplete || getPasswordHistory.loading}
                    loading={getPasswordHistory.loading}
                    fullWidth
                  >
                    <span>Consultar</span>
                  </Button>
                </ContainerFlex>
                <ContainerFlex>
                  <Button outlined onClick={() => history.goBack()}>
                    Voltar
                  </Button>
                </ContainerFlex>
              </ContainerFlex>
            </ContainerFlex>
          </ContainerFlex>
          {feedback && (
            <ContainerFlex padding={16}>
              <span>{feedback}</span>
            </ContainerFlex>
          )}
        </St.BoxCard>

        <St.BoxCard>
          <ContainerFlex row padding={16}>
            {list.length > 0 ? (
              <TableList customerHeader={customerHeader} list={list} />
            ) : (
              <ContainerFlex center marginTop={30}>
                <span>Sem registros</span>
              </ContainerFlex>
            )}
          </ContainerFlex>
          {getPasswordHistory.status === 200 &&
            getPasswordHistory.data?.totalPages > 1 && (
              <ContainerFlex row horizontal center marginTop={20}>
                <Pagination
                  totalPages={getPasswordHistory.data.totalPages}
                  onCurrentPageChange={(page) => handleConsult(page)}
                />
              </ContainerFlex>
            )}
        </St.BoxCard>
      </Container>
    </>
  );
};

export default PasswordHistory;
